"use client";
import { ArtOrderBase } from "@/api/art-order/types";
import { twMerge } from "tailwind-merge";
import { ArtOrderUser } from "../ArtOrderUser";
import TagIcon from "@/assets/icons/tag-price.svg";
import { ArtOrderDetailsItem } from "../ArtOrderDetailsItem";
import useArtOrderStatus from "../hooks/useArtOrderStatus";
import ArrowRightSmallIcon from "@/assets/icons/arrow-right-small.svg";
import ArtOrderCryptoValue from "../ArtOrderCryptoValue/ArtOrderCryptoValue";
import HeroIcon from "@/assets/graphics/hero.svg";
import { ArtOrderStatus } from "../constants";
import { getArtOrderRouteById } from "../helpers";
import { ShortUser } from "@/api/users/types";
import Link from "next/link";

export interface Props
  extends Omit<ArtOrderBase, "nonce" | "deadline" | "originalDeadline"> {
  className?: string;
  author: ShortUser;
  status: ArtOrderStatus;
}

const ArtOrderItem = ({
  id,
  author,
  title,
  shortDescription,
  maxBudget,
  budgetPaymentERC20TokenAddress,
  status,
  className,
}: Props) => {
  const { EndyIcon, textColor, message } = useArtOrderStatus(status);

  const orderDetails = [
    {
      id: 1,
      icon: (
        <TagIcon className="w-6 h-6 night:fill-white endemic:fill-secondary" />
      ),
      content: (
        <ArtOrderCryptoValue
          cryptoValue={maxBudget}
          paymentErc20TokenAddress={budgetPaymentERC20TokenAddress}
        />
      ),
    },
  ];

  return (
    <Link
      href={getArtOrderRouteById(id)}
      className={twMerge(
        className,
        "lg:h-[240px] flex flex-col lg:flex-row lg:justify-between lg:items-center px-10 xl:gap-x-16 gap-x-8 gap-y-5 lg:gap-y-0 py-6 lg:py-0 border-b border-endemicGray-500 group cursor-pointer",
      )}
    >
      {" "}
      <div className="flex flex-col lg:flex-row xl:gap-x-16 gap-x-8 gap-y-5 lg:gap-y-0 py-6 lg:items-center">
        <ArtOrderUser user={author} className="cursor-auto" />
        <div className="flex flex-col gap-y-4 lg:gap-y-4 max-w-[660px]">
          <span className={twMerge("text-body-1-sm lg:text-body-1")}>
            {title}
          </span>
          <span className="text-body-3 leading-[22px]">{shortDescription}</span>
          <span className="flex gap-x-10">
            {orderDetails.map((order) => {
              return (
                <ArtOrderDetailsItem
                  key={order.id}
                  icon={order.icon}
                  content={order.content}
                />
              );
            })}
          </span>
        </div>
      </div>
      <div className="flex items-center justify-center">
        <div className="flex flex-col items-center gap-y-2 lg:mx-10 relative w-32">
          {status === ArtOrderStatus.ACCEPTED ? (
            <HeroIcon className="hidden md:inline w-20 h-10 md:absolute md:top-[-84px] md:right-[20px] fill-green endemic:fill-white" />
          ) : null}
          {EndyIcon}
          <span className={twMerge(textColor, "whitespace-nowrap")}>
            {message}
          </span>
        </div>
        <ArrowRightSmallIcon className="fill-primary h-5 w-5 shrink-0 hidden lg:flex lg:d" />
      </div>
    </Link>
  );
};

export default ArtOrderItem;
